import { useCallback, useEffect, useState } from 'react';
import { Preferences as Storage } from '@capacitor/preferences';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';

// eslint-disable-next-line no-console
const log = (...args) => console.log('usePushNotification:', ...args);

const usePushNotification = (subjects, onNotification, { onlyOnTap } = { onlyOnTap: false }) => {
  const [notification, setNotification] = useState(null);

  const handleForegroundOrBackground = useCallback(
    (eventName, event) => {
      if (!subjects.includes(event.notification.data?.subject)) return;
      log(
        'handleForegroundOrBackground - Received notification',
        JSON.stringify(event.notification.data),
        eventName,
        subjects
      );
      onNotification(event.notification.data);
      setNotification(event.notification.data);
    },
    [subjects, onNotification]
  );

  // When iOS app is killed and launched with a notification
  const handleWheniOSAppKilled = useCallback(async () => {
    const handleFromStorage = async subject => {
      const storageKey = `notification.${subject}`;
      const { value } = await Storage.get({ key: storageKey });
      if (value === null) return;

      try {
        const data = JSON.parse(value);
        log('Read notification from storage', JSON.stringify(data), subjects);
        onNotification(data);
        setNotification(data);
      } catch {
        console.warn(`Invalid JSON in ${storageKey}, removing data.`);
      } finally {
        await Storage.remove({ key: storageKey });
      }
    };
    await Promise.all(subjects.map(handleFromStorage));
  }, [subjects, onNotification]);

  // Setup handlers
  useEffect(() => {
    // Only on mobile
    if (window.PLATFORM === 'web') return;

    if (!onlyOnTap) {
      // App is foreground
      FirebaseMessaging.addListener('notificationReceived', event =>
        handleForegroundOrBackground('notificationReceived', event)
      );
      log('Subscribed notificationReceived', subjects);
    }

    // User taps on notification
    FirebaseMessaging.addListener('notificationActionPerformed', event =>
      handleForegroundOrBackground('notificationActionPerformed', event)
    );
    log('Subscribed notificationActionPerformed', subjects);

    if (window.PLATFORM === 'ios') {
      handleWheniOSAppKilled();
      log('Subscribed to iOS storage', subjects);
    }
  }, [subjects, onNotification, handleForegroundOrBackground, handleWheniOSAppKilled, onlyOnTap]);

  return { notification };
};

export default usePushNotification;
